import { inject, NgZone, Service } from '@angular/core';
import { environment } from '@workspace/ng-config';
import { CookieService } from 'ngx-cookie-service';
import { interval, Subscription } from 'rxjs';
import { SessionStorageKey } from '../models';

@Service()
export class SessionSyncService {
  private readonly COOKIE_DOMAIN = `.${environment.domain}`;
  private readonly SIGNAL_KEY = SessionStorageKey.SESSION_ACTIVE;
  private watcher?: Subscription;

  // Store the last timestamp we saw to detect changes
  private lastKnownTimestamp: number | null = null;

  private cookieService = inject(CookieService);
  private ngZone = inject(NgZone);

  notifySessionUpdate() {
    const now = Date.now();
    this.lastKnownTimestamp = now;

    this.cookieService.set(this.SIGNAL_KEY, now.toString(), 1, '/', this.COOKIE_DOMAIN, true, 'Lax');
  }

  hasActiveSession(): boolean {
    return this.cookieService.check(this.SIGNAL_KEY);
  }

  clearSessionSignal() {
    this.cookieService.delete(this.SIGNAL_KEY, '/', this.COOKIE_DOMAIN);
    this.lastKnownTimestamp = null;
  }

  /**
   * Starts the "Multi-Tab Radar"
   * @param onSessionFound Called when user is anonymous, but a session appears elsewhere.
   * @param onSessionUpdate Called when user is already logged in, but data changed elsewhere.
   * @param onSessionLost Called when user logs out elsewhere.
   */
  startMonitoring(
    isCurrentUserLoggedIn: boolean,
    callbacks: {
      onSessionFound: () => void;
      onSessionUpdate: () => void;
      onSessionLost: () => void;
    },
  ) {
    // 1. Initialize baseline
    const initialCookie = this.cookieService.get(this.SIGNAL_KEY);
    this.lastKnownTimestamp = initialCookie ? Number.parseInt(initialCookie) : null;

    this.ngZone.runOutsideAngular(() => {
      this.watcher = interval(2000).subscribe(() => {
        const cookieValue = this.cookieService.get(this.SIGNAL_KEY);
        const cookieExists = this.cookieService.check(this.SIGNAL_KEY);
        const remoteTimestamp = cookieValue ? Number.parseInt(cookieValue) : null;

        this.ngZone.run(() => {
          // --- SCENARIO 1: Remote Logout ---
          // I am logged in, but the cookie disappeared
          if (isCurrentUserLoggedIn && !cookieExists) {
            console.log('[Sync] Remote logout detected');
            this.stopMonitoring(); // Stop to prevent loops
            callbacks.onSessionLost();
            return;
          }

          // --- SCENARIO 2: Remote Login ---
          // I am logged out, but a cookie appeared or timestamp was renewed elsewhere
          if (!isCurrentUserLoggedIn && cookieExists) {
            if (!this.lastKnownTimestamp || (remoteTimestamp && remoteTimestamp > this.lastKnownTimestamp)) {
              console.log('[Sync] Remote login detected');
              this.stopMonitoring();
              this.lastKnownTimestamp = remoteTimestamp;
              callbacks.onSessionFound();
              return;
            }
          }

          // --- SCENARIO 3: Data Refresh ---
          // I am logged in, and the timestamp changed (newer than mine)
          if (
            isCurrentUserLoggedIn &&
            remoteTimestamp &&
            this.lastKnownTimestamp &&
            remoteTimestamp > this.lastKnownTimestamp
          ) {
            console.log('[Sync] Session timestamp updated');
            this.lastKnownTimestamp = remoteTimestamp;
            callbacks.onSessionUpdate();
          }
        });
      });
    });
  }

  stopMonitoring() {
    this.watcher?.unsubscribe();
  }
}
